import { faHourglass } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import ExperienceBody from "@/mk2/apps/wiki/components/ExperienceBody";
import ExperienceFooter from "@/mk2/apps/wiki/components/ExperienceFooter";
import ExperienceHeader from "@/mk2/apps/wiki/components/ExperienceHeader";
import ExperienceMenu from "@/mk2/apps/wiki/components/ExperienceMenu";
import {
    VendorReplyOnExperienceFormData,
    VendorReplyOnExperienceFormFactory,
    vendorReplyOnExperienceFormName,
} from "@/mk2/apps/wiki/components/VendorReplyOnExperienceForm";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { Dot } from "@/mk2/components/Dot";
import { Emojify } from "@/mk2/components/Emojify";
import { Link } from "@/mk2/components/Link";
import { StarsRating } from "@/mk2/components/StarsRating";
import { ExperienceEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { STROLLERS_NOT_APPROVED } from "@/mk/autogenerated/translation";
import { upperCaseFirst } from "@/mk/bazaar/common/filters";

import styles from "./Experience.mscss";

// Cache form components by experience ID to avoid creating new component classes
// inside render(), which causes infinite DESTROY/REGISTER_FIELD loops in redux-form.
const vendorReplyFormCache = new Map<
    number,
    ReturnType<typeof VendorReplyOnExperienceFormFactory>
>();
function getVendorReplyForm(experienceId: number) {
    let Form = vendorReplyFormCache.get(experienceId);
    if (!Form) {
        Form = VendorReplyOnExperienceFormFactory(vendorReplyOnExperienceFormName(experienceId));
        vendorReplyFormCache.set(experienceId, Form);
    }
    return Form;
}

interface OwnProps {
    experience: ExperienceEntity;
    isAdmin: boolean;
    isMobile: boolean;
    showCompact?: boolean;
    location: HistoryLocationWithState;

    onDeleteVendorReply?(experienceId: number);
    onSaveVendorReply?(experienceId: number, values: VendorReplyOnExperienceFormData);
    onToggleMenuMore?(commentId: number);
}
export default class Experience extends React.Component<OwnProps> {
    public render() {
        const {
            experience,
            isAdmin,
            onDeleteVendorReply,
            onSaveVendorReply,
            onToggleMenuMore,
            showCompact,
            location,
        } = this.props;

        const VendorReplyOnExperienceForm = getVendorReplyForm(experience.id);

        return (
            <>
                <div id={`experience-${experience.id}`} className={styles.Experience}>
                    <div className={styles.Experience__wrapper}>
                        {showCompact && (
                            <h3 className={styles.Experience__wrapper__articleTitle}>
                                <Link
                                    to={getWikiArticleUrl(
                                        experience.article.category.categoryArticleSlug,
                                        experience.article.slug,
                                    )}
                                >
                                    {experience.article.title}
                                </Link>
                            </h3>
                        )}

                        <ExperienceHeader
                            className={styles.Experience__wrapper__header}
                            experience={experience}
                            showLinkToArticle={!showCompact}
                        />

                        {isAdmin && (
                            <ExperienceMenu
                                className={styles.Experience__wrapper__dropdownMenu}
                                experience={experience}
                                onToggleMenuMore={onToggleMenuMore}
                            />
                        )}

                        {experience.starsCount > 0 &&
                            experience.article.experiencesStarsAllowed && (
                                <div className={styles.Experience__starsRating}>
                                    <StarsRating starsCount={experience.starsCount} />
                                    {experience.approved === false && !experience.deleted ? (
                                        <>
                                            <Dot spaces={1} />
                                            <div className={styles.Experience__starsRating__badge}>
                                                <FontAwesomeIcon
                                                    icon={faHourglass}
                                                    className={styles.Experience__starsRating__icon}
                                                />
                                                &nbsp;
                                                {STROLLERS_NOT_APPROVED}
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            )}

                        {experience.title && (
                            <h1
                                className={cx(
                                    styles.Experience__wrapper__experienceTitle,
                                    showCompact &&
                                        styles["Experience__wrapper__experienceTitle--compact"],
                                )}
                            >
                                <Emojify>{upperCaseFirst(experience.title)}</Emojify>
                            </h1>
                        )}

                        <ExperienceBody experience={experience} showCompact={showCompact} />
                    </div>

                    {!showCompact && (
                        <ExperienceFooter
                            experience={experience}
                            isAdmin={isAdmin}
                            onDeleteVendorReply={onDeleteVendorReply}
                            location={location}
                        />
                    )}

                    {experience.article.canVendorReplyOnExperiences &&
                        !experience.vendorReplyMessage && (
                            <div className={styles.Experience__vendorReplyForm}>
                                <VendorReplyOnExperienceForm
                                    experienceId={experience.id}
                                    onSave={onSaveVendorReply}
                                />
                            </div>
                        )}
                </div>
            </>
        );
    }
}
